import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../api/axios';
import { 
  Copy, 
  Download, 
  CheckCircle, 
  Twitter,
  Linkedin,
  Facebook,
  Loader2,
  AlertCircle,
  Image,
  Quote,
  Sparkles,
  Gift,
  Music
} from 'lucide-react';
import { Button, GlassCard, Footer } from './ui';

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

function SharePage() {
  const { token } = useParams();
  const [episode, setEpisode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState({});
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchShareData = async () => {
      try {
        const response = await api.get(`/api/share/${token}`);
        setEpisode(response.data.episode);
      } catch (err) {
        setError(err.response?.data?.error || 'Invalid or expired link');
      } finally {
        setLoading(false);
      }
    };

    fetchShareData();
  }, [token]);

  const getShareText = () => {
    return `"${episode.featured_quote}"\n\n${episode.cheeky_caption}`;
  };

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareToTwitter = () => {
    const text = getShareText();
    const twitterUrl = episode?.share_urls?.twitter || `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
  };

  const shareToLinkedIn = () => {
    const linkedInUrl = episode?.share_urls?.linkedin || `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`;
    window.open(linkedInUrl, '_blank', 'width=550,height=420');
  };

  const shareToFacebook = () => {
    const facebookUrl = episode?.share_urls?.facebook || `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`;
    window.open(facebookUrl, '_blank', 'width=550,height=420');
  };

  const handleDownloadAll = async () => {
    setDownloading(true);
    try {
      // Download audiogram if available
      if (episode?.has_audiogram && episode?.audiogram_filename) {
        const link = document.createElement('a');
        link.href = `/api/download/audiogram/${episode.audiogram_filename}`;
        link.download = episode.audiogram_filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      // Download quote card if available
      if (episode?.has_quote_card && episode?.quote_card_filenames?.[0]) {
        await new Promise(resolve => setTimeout(resolve, 500));
        const link = document.createElement('a');
        link.href = `/api/download/image/${episode.quote_card_filenames[0]}`;
        link.download = episode.quote_card_filenames[0];
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } finally {
      setDownloading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleWebShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: episode.title,
          text: getShareText(),
          url: window.location.href
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
        }
      }
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950" />
        <motion.div 
          className="relative z-10 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="w-16 h-16 mx-auto mb-6 relative">
            <div className="absolute inset-0 rounded-full border-2 border-emerald-500/20" />
            <div className="absolute inset-0 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
          </div>
          <p className="text-zinc-400">Loading your promotion kit...</p>
        </motion.div>
      </div>
    );
  }

  // Error State
  if (error || !episode) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950" />
        <GlassCard className="relative z-10 max-w-md w-full text-center p-8">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-red-500/10 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-zinc-100 mb-2">Link Not Found</h2>
          <p className="text-zinc-400">{error || 'This promotion kit link is invalid or has expired.'}</p>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl" />
      
      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-emerald-500/30 rounded-full"
            initial={{ 
              x: Math.random() * 1920, 
              y: Math.random() * 1080 
            }}
            animate={{ 
              y: [null, Math.random() * -200 - 100],
              opacity: [0.3, 0]
            }}
            transition={{ 
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      <motion.div 
        className="relative z-10 px-4 py-8 md:py-12 max-w-4xl mx-auto"
        variants={staggerContainer}
        initial="initial"
        animate="animate"
      >
        {/* Header */}
        <motion.div 
          className="text-center mb-10"
          variants={fadeInUp}
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Gift className="w-4 h-4" />
            Your Promotion Kit is Ready
          </motion.div>
          
          <h1 className="text-3xl md:text-5xl font-bold text-zinc-100 mb-4">
            Share Your Episode
          </h1>
          
          <p className="text-lg text-zinc-400">
            with <span className="text-emerald-400 font-medium">{episode.guest_name}</span>
          </p>
        </motion.div>

        {/* Episode Title Card */}
        <motion.div variants={fadeInUp}>
          <GlassCard className="mb-8 p-6 md:p-8 text-center border-emerald-500/20">
            <h2 className="text-xl md:text-2xl font-semibold text-zinc-100">
              {episode.title}
            </h2>
          </GlassCard>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid gap-6 md:gap-8">
          {/* Audiogram Section */}
          {episode.has_audiogram && episode.audiogram_filename && (
            <motion.div variants={fadeInUp}>
              <GlassCard className="overflow-hidden">
                <div className="p-6 border-b border-zinc-800/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <Music className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-zinc-100">Video Audiogram</h3>
                      <p className="text-sm text-zinc-400">Share this on social media</p>
                    </div>
                  </div>
                </div>
                
                {episode.audiogram_quote && (
                  <div className="px-6 py-4 bg-zinc-900/50 border-b border-zinc-800/50">
                    <p className="text-zinc-300 italic text-center">
                      "{episode.audiogram_quote}"
                    </p>
                  </div>
                )}

                <div className="aspect-video bg-zinc-950">
                  <video 
                    controls 
                    className="w-full h-full object-contain"
                    src={`/api/audiograms/${episode.audiogram_filename}`}
                    poster=""
                  />
                </div>

                <div className="p-4 flex justify-center">
                  <Button
                    variant="secondary"
                    icon={Download}
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = `/api/download/audiogram/${episode.audiogram_filename}`;
                      link.download = episode.audiogram_filename;
                      link.click();
                    }}
                  >
                    Download Video
                  </Button>
                </div>
              </GlassCard>
            </motion.div>
          )}

          {/* Quote Card Section */}
          {episode.has_quote_card && episode.quote_card_filenames?.[0] && (
            <motion.div variants={fadeInUp}>
              <GlassCard className="overflow-hidden">
                <div className="p-6 border-b border-zinc-800/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <Image className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-zinc-100">Quote Card</h3>
                      <p className="text-sm text-zinc-400">Perfect for Instagram & LinkedIn</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-zinc-950 flex justify-center">
                  <img 
                    src={`/api/images/${episode.quote_card_filenames[0]}`}
                    alt="Quote Card"
                    className="max-w-full max-h-[500px] rounded-xl"
                  />
                </div>

                <div className="p-4 flex justify-center">
                  <Button
                    variant="secondary"
                    icon={Download}
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = `/api/download/image/${episode.quote_card_filenames[0]}`;
                      link.download = episode.quote_card_filenames[0];
                      link.click();
                    }}
                  >
                    Download Image
                  </Button>
                </div>
              </GlassCard>
            </motion.div>
          )}

          {/* Featured Quote (Text) */}
          {(!episode.has_quote_card || !episode.quote_card_filenames?.[0]) && episode.featured_quote && (
            <motion.div variants={fadeInUp}>
              <GlassCard className="p-8 relative overflow-hidden">
                <div className="absolute top-4 left-4 text-emerald-500/20">
                  <Quote className="w-16 h-16" />
                </div>
                <div className="relative z-10">
                  <p className="text-xl md:text-2xl text-zinc-100 font-medium leading-relaxed text-center italic">
                    "{episode.featured_quote}"
                  </p>
                </div>
                <div className="absolute bottom-4 right-4 text-emerald-500/20 rotate-180">
                  <Quote className="w-16 h-16" />
                </div>
              </GlassCard>
            </motion.div>
          )}

          {/* Caption Section */}
          <motion.div variants={fadeInUp}>
            <GlassCard className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-zinc-100">Ready-to-Post Caption</h3>
                </div>
                <Button
                  variant={copied.caption ? 'primary' : 'secondary'}
                  size="sm"
                  icon={copied.caption ? CheckCircle : Copy}
                  onClick={() => copyToClipboard(episode.cheeky_caption, 'caption')}
                >
                  {copied.caption ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              <div className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800/50">
                <p className="text-zinc-300 whitespace-pre-wrap leading-relaxed">
                  {episode.cheeky_caption}
                </p>
              </div>
            </GlassCard>
          </motion.div>

          {/* Social Share Buttons */}
          <motion.div variants={fadeInUp}>
            <GlassCard className="p-6">
              <h3 className="text-lg font-semibold text-zinc-100 mb-6 text-center">
                Share on Social Media
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <motion.button
                  onClick={shareToTwitter}
                  className="flex items-center justify-center gap-3 p-4 rounded-xl bg-[#1DA1F2]/10 border border-[#1DA1F2]/20 text-[#1DA1F2] hover:bg-[#1DA1F2]/20 transition-all"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Twitter className="w-5 h-5" />
                  <span className="font-medium">Twitter/X</span>
                </motion.button>

                <motion.button
                  onClick={shareToLinkedIn}
                  className="flex items-center justify-center gap-3 p-4 rounded-xl bg-[#0077B5]/10 border border-[#0077B5]/20 text-[#0077B5] hover:bg-[#0077B5]/20 transition-all"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Linkedin className="w-5 h-5" />
                  <span className="font-medium">LinkedIn</span>
                </motion.button>

                <motion.button
                  onClick={shareToFacebook}
                  className="flex items-center justify-center gap-3 p-4 rounded-xl bg-[#1877F2]/10 border border-[#1877F2]/20 text-[#1877F2] hover:bg-[#1877F2]/20 transition-all"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Facebook className="w-5 h-5" />
                  <span className="font-medium">Facebook</span>
                </motion.button>
              </div>

              {/* Copy All Button */}
              <div className="flex justify-center">
                <Button
                  variant={copied.all ? 'primary' : 'secondary'}
                  icon={copied.all ? CheckCircle : Copy}
                  onClick={() => copyToClipboard(getShareText(), 'all')}
                >
                  {copied.all ? 'Copied to Clipboard!' : 'Copy Quote & Caption'}
                </Button>
              </div>
            </GlassCard>
          </motion.div>

          {/* Download All Section */}
          {(episode.has_audiogram || episode.has_quote_card) && (
            <motion.div 
              variants={fadeInUp}
              className="text-center"
            >
              <Button
                size="xl"
                icon={downloading ? Loader2 : Download}
                loading={downloading}
                onClick={handleDownloadAll}
                className="min-w-[250px]"
              >
                Download All Assets
              </Button>
            </motion.div>
          )}

          {/* Pro Tip */}
          <motion.div variants={fadeInUp}>
            <div className="p-6 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border border-amber-500/20 rounded-2xl">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-amber-300 font-medium mb-1">Pro Tip</h4>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    Use the "Copy Quote & Caption" button to paste this content into Instagram, TikTok, or any other platform! Download the graphics to share them your way.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <Footer centered={true} />
      </motion.div>
    </div>
  );
}

export default SharePage;
